/**
 * CAMPAIGN W1.6 — ACT 1: VERIFY THE NAME. Beat data only (pure constants).
 * Prose adapted from 05-story-arc.md (L25-48) + 06-screenplay-scenes.md
 * Scenes 2-3. Per Ruling 5, the a1-survivor beacon is the technician SELE OKONKWO
 * (the second, distinct living-dead) — an authored carved record running the SAME
 * verification machinery as the procedural HALIS VORN derelict. Halis Tendrey's
 * own contradiction now resolves at the explosion (the murder makes the wall true).
 *
 * NOTE (adaptation): the screenplay's named Drum dogfight is the Act-2 Saturn
 * climax; wave 1 captures the mercy FLAG here on the unmarked cutter — Act 2
 * may reveal the cutter's captain or stage the rematch; the flag key is final.
 */
import type { CampaignBeat } from './campaign.js';
import { CENOTAPH_SIGNAL } from './act0.js';

/** Phobos drydock — Mars-anchored (Phobos itself isn't in the manifest). */
const PHOBOS_DRYDOCK = {
  id: 'camp-phobos-drydock',
  anchor: { body: 'Mars' },
  offsetKm: { x: 9_400, y: 2_100, z: 3_200 }, // Phobos-orbit altitude band
  kind: 'memorial' as const,
  title: 'PHOBOS DRYDOCK — LANE FOUR',
  threat: 0,
  seed: 477101,
};

/** The Light Mule's wreck, drifting just off the departure lane. */
const LIGHT_MULE_WRECK = {
  id: 'camp-light-mule-wreck',
  anchor: { body: 'Mars' },
  offsetKm: { x: 11_800, y: 2_900, z: 4_600 },
  kind: 'wreckfield' as const,
  title: 'LANE FOUR — INCIDENT 8841-P',
  threat: 0,
  seed: 477102,
  record: {
    name: 'LIGHT MULE',
    log: 'unscheduled fusion breach, lane four. emergency response dispatched. no recoverable crew. cause: under review. — Concord incident record 8841-P',
  },
};

/** The living name: a census-active beacon, deep belt-ward of Mars. */
const CERES_SURVIVOR = {
  id: 'camp-ceres-survivor',
  anchor: { body: 'Mars' },
  offsetKm: { x: 128_000, y: -22_000, z: 64_000 },
  kind: 'derelict' as const,
  title: 'CENSUS-ACTIVE BEACON',
  threat: 0,
  seed: 477103,
  record: {
    // §5 (Ruling 5) — the second living name is the technician, not a duplicate
    // Halis. SELE OKONKWO is a placeholder (owner may rename). Text-only record.
    name: 'SELE OKONKWO',
    log: 'census record active. subject is still alive — former Concord trauma-ward technician, off-grid belt-ward. carved regardless. fourth name on the list, circled. — verification copy, Guild archive',
  },
};

/**
 * The unmarked cutter interdiction (threat 2 → two waves). SHIP-EXPANSION: the
 * hostiles fly Captain Drum's `hellebore` — the matte-black Concord cutter — so
 * the Vesper hunters read as a single faction-correct hull (and render real art).
 */
const BLACK_CUTTER = {
  id: 'camp-black-cutter',
  anchor: { body: 'Mars' },
  offsetKm: { x: 133_000, y: -20_000, z: 67_500 },
  kind: 'ambush' as const,
  title: 'INTERDICTION — UNMARKED CUTTER',
  threat: 2,
  seed: 477104,
  faction: 'concord' as const,
  enemyType: 'hellebore',
};

export const ACT1_BEATS: CampaignBeat[] = [
  {
    id: 'a1-title',
    act: 1,
    chapterCard: { title: 'BLACK MARBLE', sub: 'ACT 1 — VERIFY THE NAME' },
    storySignals: [CENOTAPH_SIGNAL, PHOBOS_DRYDOCK],
    advance: [{ on: 'DROPPED_AT_SIGNAL', target: 'camp-phobos-drydock', next: 'a1-halis' }],
  },
  {
    id: 'a1-halis',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL, PHOBOS_DRYDOCK],
    comms: {
      id: 'c-halis',
      lines: [
        { speaker: 'LANE FOUR', text: 'Observation gallery. Courier ships in cradles below, Aresian engineers in iron-oxide coveralls. The light is the rust-pink of midday Mars.' },
        { speaker: 'HALIS', text: "You're not joking." },
        { speaker: 'VEY', text: "I'm not joking." },
        { speaker: 'HALIS', text: "I'm on a wall." },
        { speaker: 'VEY', text: "You're on the wall." },
        { speaker: 'HALIS', text: 'The wall. The wall wall. The big sad black ball wall.' },
        { speaker: 'VEY', text: 'That wall.' },
        { speaker: 'HALIS', text: 'Concord coffee.' },
        { speaker: 'VEY', text: 'It was the best I could do.' },
        { speaker: 'HALIS', text: "You came all the way from the Cenotaph to tell me I'm dead." },
        { speaker: 'VEY', text: "To ask you if you'd like to be removed." },
        { speaker: 'HALIS', text: 'Huh.' },
        { speaker: 'HALIS', text: "My grandmother's on the wall. For real. Surgeon. The Silence got her. Mid-op." },
        { speaker: 'VEY', text: "I'm sorry." },
        { speaker: 'HALIS', text: 'You ever think about how we say that. Got her. Like it was a person.' },
        { speaker: 'VEY', text: 'Every day.' },
        { speaker: 'HALIS', text: 'You really came out here for a clerical error.' },
        { speaker: 'VEY', text: "We don't think it's a clerical error." },
        { speaker: 'HALIS', text: 'Then what is it.' },
        { speaker: 'VEY', text: "We don't know yet." },
        { speaker: 'HALIS', text: "I've got a run to Ceres in three hours. Water and seal-rings, milk run. Get coffee that doesn't taste like a courtroom and I'll tell you my whole stupid life." },
        { speaker: 'VEY', text: 'Deal.' },
        { speaker: 'HALIS', text: "Hey, Nameswriter. Tell the wall I'm not done yet." },
      ],
    },
    advance: [{ on: 'COMMS_DONE', target: 'c-halis', next: 'a1-explosion' }],
  },
  {
    id: 'a1-explosion',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL, PHOBOS_DRYDOCK, LIGHT_MULE_WRECK],
    comms: {
      id: 'c-explosion',
      lines: [
        { speaker: 'LANE FOUR', text: 'Two hours later. The LIGHT MULE lifts off cradle seven. Clears the drydock envelope. Main drive lights. For half a second, the drive is correct.' },
        { speaker: 'LANE FOUR', text: "Then it isn't." },
        { speaker: 'LANE FOUR', text: 'A flash — small, white-hot, contained — at the aft quarter. Then the silent, total bloom of an unscheduled fusion breach.' },
        { speaker: 'PA VOICE', text: 'Attention. A vessel incident has occurred in lane four. Emergency response dispatched. Please remain calm.' },
        // §5d — the AAA-01 Halis flip moves here: the murder is what makes the wall
        // "true." TEXT-ONLY (LEDGER); frees a1-vesper for Okonkwo, the true second flip.
        { speaker: 'LEDGER', text: 'HALIS TENDREY — CONTRADICTION RESOLVED. Census: alive this morning. Wall: dead three weeks. Lane four closes the gap. The record is now true.' },
        // §11 — "it is" → "the record is" (legibility only; no continuity error). The
        // two VEY clips are pinned so the inserted LEDGER line doesn't shift their files.
        { speaker: 'VEY', voice: 'vey-c-explosion-04', text: 'Jem. The census was right and now the record is wrong. You understand me. The record changed three hours after I read it to his face.' },
        { speaker: 'VEY', voice: 'vey-c-explosion-05', text: 'I am coming back. Do not carve anything.' },
      ],
    },
    objective: {
      id: 'camp-obj-return',
      kind: 'investigate',
      status: 'ACTIVE',
      target: 'story-cenotaph-vigil',
      progress: 0,
      goal: 1,
      reward: 0,
      briefing: 'RETURN TO THE CENOTAPH — Jem needs to hear it in person.',
    },
    advance: [{ on: 'DROPPED_AT_SIGNAL', target: 'story-cenotaph-vigil', next: 'a1-jem' }],
  },
  {
    id: 'a1-jem',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL],
    rollEntry: 'JEM KESTREL-NINE',
    comms: {
      id: 'c-jem',
      lines: [
        { speaker: 'GUILD NOTICE', text: 'NAMESWRITER KESTREL-NINE, JEM. FOUND IN AIRLOCK SIX, UNSUITED. RULED: SELF-RELEASE. THE GUILD MOURNS ITS OWN.' },
        { speaker: 'VEY', text: 'Jem did not walk into an airlock. Jem spent seventy-three years refusing to come down off the wall.' },
        { speaker: 'VEY', text: 'The slate is gone. The stylus is gone. The box he kept under the carving rig is gone.' },
        { speaker: 'GUILD NOTICE', text: 'PERSONAL EFFECTS: ITEMS IN GUILD CUSTODY MAY BE CLAIMED AT HIGH GUARD ORBITAL, EVIDENCE LOCKER FOUR.' },
        { speaker: 'VEY', text: 'Evidence locker four. Then someone wanted it found.' },
        { speaker: 'VEY', text: 'Or someone is watching who comes to claim it.' },
      ],
    },
    objective: {
      id: 'camp-obj-locker',
      kind: 'deliver',
      status: 'ACTIVE',
      target: 'high-guard',
      progress: 0,
      goal: 1,
      reward: 0,
      briefing: 'EVIDENCE LOCKER FOUR — dock at High Guard Orbital and claim what Jem left.',
    },
    advance: [{ on: 'DOCKED_AT', target: 'high-guard', next: 'a1-kira' }],
  },
  {
    id: 'a1-kira',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL],
    comms: {
      id: 'c-kira',
      lines: [
        { speaker: 'LOCKER FOUR', text: "Jem's box: a dead slate, a spare stylus, and a paper list in a careful hand. Four names. Three carved. One circled — yours to find." },
        // §6c — Jem's recorded slate line (NEW voiced line, JEM). No goodbye; ends on
        // instruction; seeds the four-name structure and Jem's guilt. Inserting it here
        // shifts every later line, so the already-generated clips below are pinned.
        { speaker: 'JEM', voice: 'jem-c-kira-00a', text: "Vey. If this woke for you, I've come down off the wall. The three carved names are mine to answer — I cut them before I understood. The fourth is yours. Carve true, and slower than you want to." },
        { speaker: 'KÍRA', voice: 'kira-c-kira-01', text: "You're holding that like it's going to bite you." },
        { speaker: 'VEY', voice: 'vey-c-kira-02', text: 'Who are you.' },
        { speaker: 'KÍRA', voice: 'kira-c-kira-03', text: 'Kíra Nández-Spún. I fly escort for people the lanes have stopped being polite to. You qualify twice over.' },
        { speaker: 'VEY', voice: 'vey-c-kira-04', text: 'I did not hire an escort.' },
        { speaker: 'KÍRA', voice: 'kira-c-kira-05', text: 'No. Jem did. Two weeks ago. Paid through Ceres-Hollow, which means he knew better than to trust a Concord ledger with it.' },
        { speaker: 'KÍRA', voice: 'kira-c-kira-06', text: "You're going to die out here, Nameswriter." },
        { speaker: 'VEY', voice: 'vey-c-kira-07', text: 'I know.' },
        { speaker: 'KÍRA', voice: 'kira-c-kira-08', text: 'Doesn\'t bother you.' },
        { speaker: 'VEY', voice: 'vey-c-kira-09', text: 'It would have. A month ago. Now I am worried about the wrong thing.' },
        { speaker: 'KÍRA', voice: 'kira-c-kira-10', text: 'Which wrong thing.' },
        { speaker: 'VEY', voice: 'vey-c-kira-11', text: 'Whether the wall is true.' },
        { speaker: 'KÍRA', voice: 'kira-c-kira-12', text: "So. The list in your hand. Do I get to see what I'm flying into, or do we do this the Concord way." },
      ],
      // §7 (Ruling 4) — the kiraTrust choice is GONE from this beat. Kíra has not
      // yet earned the list; the meeting ends on her hanging question and advances
      // when the comms closes. The trust decision is relocated to a1-trust, after
      // she flies the interdiction as escort (below).
    },
    advance: [{ on: 'COMMS_DONE', target: 'c-kira', next: 'a1-survivor' }],
  },
  {
    id: 'a1-survivor',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL, CERES_SURVIVOR],
    objective: {
      id: 'camp-obj-survivor',
      kind: 'investigate',
      status: 'ACTIVE',
      target: 'camp-ceres-survivor',
      progress: 0,
      goal: 1,
      reward: 0,
      briefing: "THE SECOND LIVING NAME — the circled name off Jem's list, a census-active beacon deep belt-ward. Drop in and VERIFY [V]. Carve true.",
    },
    advance: [{ on: 'VERIFIED', target: 'camp-ceres-survivor', next: 'a1-vesper' }],
  },
  {
    id: 'a1-vesper',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL, CERES_SURVIVOR],
    comms: {
      id: 'c-vesper',
      lines: [
        // §5c — the contradiction reads Okonkwo now (Halis's flip moved to the explosion).
        { speaker: 'LEDGER', text: 'SELE OKONKWO — CONTRADICTION. The census says alive. The wall says dead. Both are official. Both are signed.' },
        { speaker: 'KÍRA', text: "That's the second living person you've found carved into a tomb. There's a word for a list of names somebody needs dead on paper." },
        { speaker: 'VEY', text: 'Say it.' },
        { speaker: 'KÍRA', text: 'A budget line. Jem\'s paper list had a heading torn off. The fold pattern says one word, eight letters or so. Project something.' },
        { speaker: 'UNKNOWN', text: '— is an administrative correction program. You were never meant to read the wall that closely. Stop reading the wall that closely. This is the only courtesy transmission you will rece—' },
        { speaker: 'KÍRA', text: 'Transmission cut. No origin header. Concord encryption, civilian skin.' },
        { speaker: 'VEY', text: 'Vesper. The heading said Vesper.' },
        // §9 — fire the Vesper-3's name (NEW voiced line). The question hangs; Vey does
        // not answer in Act 1. Pinned clip; it shifts the combat callout below, also pinned.
        { speaker: 'KÍRA', voice: 'kira-c-vesper-06b', text: 'Wait. The Vesper-3. Who named your ship, Nameswriter.' },
        { speaker: 'KÍRA', voice: 'kira-c-vesper-07', text: "Heads up — I've got iron on the scope. One hull, running dark, no transponder. It was waiting on the beacon." },
      ],
    },
    advance: [{ on: 'COMMS_DONE', target: 'c-vesper', next: 'a1-dogfight' }],
  },
  {
    id: 'a1-dogfight',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL, CERES_SURVIVOR, BLACK_CUTTER],
    // Point the HUD at the cutter so the interdiction reads as a place to fly to,
    // not just a line of text. target stays '' (bounty matches on faction).
    markerSignalId: 'camp-black-cutter',
    objective: {
      id: 'camp-obj-cutter',
      kind: 'bounty',
      status: 'ACTIVE',
      target: '',
      progress: 0,
      goal: 2,
      reward: 0,
      briefing:
        'THE BLACK CUTTER — an unmarked hull waits on the beacon. Fly to the marker [N] and clear 2 combat waves.',
    },
    advance: [{ on: 'ENCOUNTER_CLEARED', target: 'camp-black-cutter', next: 'a1-trust' }],
  },
  {
    // §7 (Ruling 4) — the relocated kiraTrust choice. It lands HERE, after Kíra has
    // put her hull between the player and a Vesper gun (a1-dogfight), so the trust
    // is earned, not demanded on first meeting. Same flag key + values as the
    // retired a1-kira menu — save-compat safe. New voiced lead-in (auto-derived
    // slug kira-c-trust-00; stale/ungenerated until the operator-gated VO batch).
    id: 'a1-trust',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL],
    comms: {
      id: 'c-trust',
      lines: [
        { speaker: 'KÍRA', text: 'I put my hull between you and a Vesper gun back there. Now. The list. Do I see it, or not.' },
      ],
      choices: [
        { key: '1', label: 'SHOW HER THE FILES', setFlag: { key: 'kiraTrust', value: true } },
        { key: '2', label: 'KEEP THEM CLOSED', setFlag: { key: 'kiraTrust', value: false } },
      ],
    },
    advance: [{ on: 'CHOICE', target: 'c-trust', next: 'a1-mercy' }],
  },
  {
    id: 'a1-mercy',
    act: 1,
    storySignals: [CENOTAPH_SIGNAL],
    comms: {
      id: 'c-mercy',
      lines: [
        { speaker: 'KÍRA', text: 'Last one is drifting. Drive dead, cabin holding. No transponder, no colors, no callsign. Nobody will ever know either way.' },
        { speaker: 'VEY', text: 'Somebody paints a hull black and sends it to kill couriers. Somebody else flies it.' },
        { speaker: 'KÍRA', text: 'Your call, Nameswriter. Either way his name ends up on a panel. The only question is whose hand cuts it.' },
      ],
      choices: [
        { key: '1', label: 'FINISH IT', setFlag: { key: 'drumMercy', value: 'killed' } },
        { key: '2', label: 'LEAVE IT DRIFTING', setFlag: { key: 'drumMercy', value: 'spared' } },
      ],
    },
    advance: [{ on: 'CHOICE', target: 'c-mercy', next: 'a1-close' }],
  },
  {
    id: 'a1-close',
    act: 1,
    chapterCard: { title: 'THE BELT', sub: 'EVERY CONCORD CHANNEL IS CLOSED TO YOU NOW' },
    storySignals: [CENOTAPH_SIGNAL],
    comms: {
      id: 'c-close',
      lines: [
        { speaker: 'VEY', text: 'Four names on a paper list. Three carved by a man who is now carved himself. One alive, twice over, in two official records that cannot both be true.' },
        { speaker: 'VEY', text: 'The census says alive. The wall says dead. Somewhere there is a desk where those two facts are the same decision.' },
        { speaker: 'VEY', text: 'Jem. I am going to find the desk.' },
        { speaker: 'KÍRA', text: 'Belt-ward, then. There\'s a broker on Ceres who owes me a favor and a straight answer, and nobody in the Hollow asks a Concord question twice. Point us at the desk.' },
      ],
    },
    advance: [],
  },
];
